/**
 * OwnerInventoryQrSheet — printable grid of SpLink QR labels for every
 * active lot in a business. Owners hit Cmd/Ctrl+P (or the big Print
 * button) to produce shelf tags, crate labels, or packaging inserts.
 *
 * Route: /food-pulse/owner/inventory/qr-sheet?business_id=…
 *
 * A print stylesheet strips chrome (nav, buttons, breadcrumbs) so the
 * printed page shows only the QR grid with clean margins.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer, Loader2, QrCode, RefreshCw } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeShortUrl, normalizeAssetUrl, synthesizeQrFromShortUrl, displayShortUrl } from "@/lib/splinkUrl";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";

function QrLabel({ lot, onEnsureQr }) {
  const [minting, setMinting] = useState(false);
  const shortUrl = normalizeShortUrl(lot.short_url);
  const qrUrl    = normalizeAssetUrl(
    lot.qr_url || synthesizeQrFromShortUrl(shortUrl),
    API_BASE,
  );
  const mint = async () => {
    setMinting(true);
    try { await onEnsureQr(lot.id); } catch { /* noop */ }
    setMinting(false);
  };
  return (
    <div className="qr-label border rounded-xl p-4 bg-white flex flex-col items-center text-center" style={{ borderColor: "#E9D8B4" }} data-testid={`qr-label-${lot.id}`}>
      {qrUrl ? (
        <img src={qrUrl} alt={`QR for ${lot.product_name}`} className="w-32 h-32 mb-2" />
      ) : (
        <div className="w-32 h-32 mb-2 flex items-center justify-center border rounded-lg" style={{ borderColor: "#E9D8B4" }}>
          {minting
            ? <Loader2 className="animate-spin" size={28} />
            : <button onClick={mint} className="text-xs font-semibold flex flex-col items-center gap-1" style={{ color: GREEN }} data-testid={`mint-qr-${lot.id}`}>
                <QrCode size={20} /> Generate
              </button>
          }
        </div>
      )}
      <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: GREEN }}>Lot {lot.lot_no || lot.id.slice(0, 6)}</div>
      <div className="text-sm font-bold leading-tight mt-0.5" style={{ color: GREEN }}>{lot.product_name || "—"}</div>
      <div className="text-[11px] text-gray-600 mt-0.5">{lot.current_qty ?? 0} {lot.unit || "each"}{lot.expiry ? ` · exp ${lot.expiry.slice(0, 10)}` : ""}</div>
      {shortUrl && (
        <a href={shortUrl} target="_blank" rel="noreferrer" className="text-[10px] font-mono mt-1 truncate max-w-full hover:underline" style={{ color: GREEN }}>
          {displayShortUrl(shortUrl)}
        </a>
      )}
    </div>
  );
}

export default function OwnerInventoryQrSheet() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const businessId = params.get("business_id") || "";
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const r = await http.get("/inventory/lots", { params: { business_id: businessId, limit: 500 } });
      setLots(r.data.items || []);
    } catch (e) { console.warn(e); }
    setLoading(false);
  };
  useEffect(() => { if (user) reload(); /* eslint-disable-next-line */ }, [user, businessId]);

  const ensureQr = async (lotId) => {
    const r = await http.post(`/inventory/lots/${lotId}/qr`, {});
    setLots((prev) => prev.map((l) => l.id === lotId ? { ...l, qr_url: r.data.qr_url, short_url: r.data.short_url } : l));
  };

  const stats = useMemo(() => {
    const withQr = lots.filter((l) => l.qr_url).length;
    return { total: lots.length, withQr, missing: lots.length - withQr };
  }, [lots]);

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!user) return <div className="p-10 text-center">Please sign in to print QR labels.</div>;
  if (!businessId) return <div className="p-10 text-center">Missing business id in URL.</div>;

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="qr-sheet-page">
      {/* Print CSS — hides chrome, gives the sheet clean A4 margins */}
      <style>{`
        @media print {
          header, footer, .no-print { display: none !important; }
          body, main { background: white !important; }
          .qr-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; gap: 12px !important; }
          .qr-label { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="no-print flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <Link to={`/food-pulse/owner/inventory?business_id=${businessId}`} className="inline-flex items-center gap-1 text-xs font-semibold mb-2" style={{ color: GREEN }} data-testid="back-to-dashboard">
              <ArrowLeft size={12} /> Back to inventory
            </Link>
            <h1 className="text-2xl font-black" style={{ color: GREEN }}>QR label print sheet</h1>
            <p className="text-sm text-gray-600">
              {stats.total} active lots · {stats.withQr} labels ready
              {stats.missing > 0 ? ` · ${stats.missing} missing (tap the tile to mint)` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={reload} className="rounded-full border px-3 py-2 text-sm" style={{ borderColor: GREEN, color: GREEN }} data-testid="reload-btn">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={() => window.print()} className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2" style={{ background: GREEN, color: CREAM }} data-testid="print-btn">
              <Printer size={16} /> Print / Save as PDF
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></div>
        ) : lots.length === 0 ? (
          <div className="rounded-2xl border p-10 text-center bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="empty-state">
            <div className="text-gray-600 mb-3">No active lots to print.</div>
            <Link to={`/food-pulse/owner/inventory/receive?business_id=${businessId}`} className="rounded-full px-4 py-2 text-sm font-semibold" style={{ background: GREEN, color: CREAM }}>
              Receive your first lot
            </Link>
          </div>
        ) : (
          <div className="qr-grid grid gap-3 md:grid-cols-3 lg:grid-cols-4" data-testid="qr-grid">
            {lots.map((l) => <QrLabel key={l.id} lot={l} onEnsureQr={ensureQr} />)}
          </div>
        )}
      </div>
    </main>
  );
}
